import { createEffect, createSignal, type JSX, on, Show } from 'solid-js';
import type { HudItem } from '../lib/fleet-live';
import { canAnimate, powerOff, powerOn } from '../lib/fleet-live-power';
import { FleetLiveHud } from './FleetLiveHud';

// One Fleet Live panel's chrome (#6778, #6779; prototype `.lp-wrap` / `.lp` / `.lp-h`): the
// article, its h3, its HUD and its controls (the grip, "Place" and "Hide",
// `FleetLivePanelControls`). Core Intelligence has no header row (`headless`): its controls
// sit on its stage's top right, and its body draws its own h3. Shown and hidden through the
// power animation (`fleet-live-power.ts`) unless motion is reduced, when it shows and hides
// at once. `wrapRef` hands the section the wrap it places into a zone (`fleet-live-zones.ts`).

export type FleetLivePanelProps = {
  readonly title: string;
  readonly shown: boolean;
  readonly motion: boolean;
  readonly hud?: readonly HudItem[];
  readonly controls: JSX.Element;
  readonly headless?: boolean;
  readonly wrapRef?: (el: HTMLDivElement) => void;
  /** Ran once a show or hide has finished (the zones re-read which hold a shown panel). */
  readonly onSettled?: () => void;
  readonly children: JSX.Element;
};

export function FleetLivePanel(props: FleetLivePanelProps): JSX.Element {
  let wrap: HTMLDivElement | undefined;
  const [hidden, setHidden] = createSignal(!props.shown);
  createEffect(
    on(
      () => props.shown,
      (shown) => {
        const el = wrap;
        if (!props.motion || !canAnimate(el)) {
          setHidden(!shown);
          props.onSettled?.();
          return;
        }
        if (shown) {
          setHidden(false);
          props.onSettled?.();
          powerOn(el);
        } else
          powerOff(el, () => {
            setHidden(true);
            props.onSettled?.();
          });
      },
      { defer: true },
    ),
  );
  return (
    <div
      class="fleet-live__wrap"
      ref={(el) => {
        wrap = el;
        props.wrapRef?.(el);
      }}
      hidden={hidden()}
    >
      <article class="fleet-live__panel" aria-label={props.title}>
        <Show when={!props.headless}>
          <div class="fleet-live__head">
            <h3 class="fleet-live__title">{props.title}</h3>
            <Show when={props.hud}>{(hud) => <FleetLiveHud items={hud()} />}</Show>
            {props.controls}
          </div>
        </Show>
        {props.children}
      </article>
    </div>
  );
}
